import {
  IsDefined,
  IsString,
  IsEmail,
  IsBoolean,
  IsEnum,
  IsOptional,
  Length,
  ValidateIf,
} from 'class-validator';

enum FlatContactType {
  resident = 'resident',
  residentOwner = 'resident-owner',
  owner = 'owner',
  movedSold = 'moved-sold',
  noAssociation = 'no-association',
  other = 'other',
  lettingAgent = 'letting-agent',
  onBehalfOf = 'on-behalf-of',
}

/**
 * Return true if a contact is 16 or over.
 * They must not be an owner and must be a resident.
 * `data` is truly `any` as no validation has happened at this point.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const flatContactIs16OrOver = (data: any): boolean =>
  !data.isUnder16 || !!data.isOwner || !data.isResident;

export class NewFlatContactDTO {
  constructor(data: NewFlatContactDTO) {
    Object.assign(this, data);
  }

  @IsDefined({ message: 'id is required.' })
  @IsString()
  id!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'firstName is required.' })
  @IsString()
  @Length(1, 50)
  firstName!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'lastName is required.' })
  @IsString()
  @Length(1, 50)
  lastName!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o) && o.email !== '')
  @IsEmail()
  email!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o) && o.phone !== '')
  @Length(3, 20)
  phone!: string;

  @IsDefined({ message: 'isResident is required.' })
  @IsBoolean()
  isResident!: boolean;

  @IsDefined({ message: 'isOwner is required.' })
  @IsBoolean()
  isOwner!: boolean;

  // Validate contactType if contact is not an owner or resident
  @ValidateIf((o) => !o.isOwner && !o.isResident)
  @IsEnum(FlatContactType)
  contactType!: FlatContactType;

  @IsString()
  @Length(0, 1000)
  internalNotes!: string;

  @IsString()
  @Length(0, 1000)
  comments!: string;

  @IsDefined({ message: 'isUnder16 is required.' })
  @IsBoolean()
  isUnder16!: boolean;

  @IsDefined({ message: 'hasSupportNeeds is required.' })
  @IsBoolean()
  hasSupportNeeds!: boolean;

  @IsBoolean()
  hasMobilityLimitation!: boolean;

  @IsBoolean()
  hasCognitiveImpairment!: boolean;

  @IsBoolean()
  hasHearingImpairment!: boolean;

  @IsBoolean()
  hasSocialAnxiety!: boolean;

  @IsBoolean()
  hasEmergencyEvacuationStatement!: boolean;

  @IsBoolean()
  hasGivenSupportNeedsConsent!: boolean;

  @IsOptional()
  @IsBoolean()
  isPrimary?: boolean;

  @IsOptional()
  @IsBoolean()
  isContactable?: boolean;
}
